// Remote access (P6): access targets, sessions, transcripts, snippets, and
// the terminal socket (docs/modules/remote-access.md).
import { ApiError, call, callNoContent } from './fleetApi';

export type TargetProtocol = 'http' | 'https' | 'tcp';

export interface AccessTarget {
  id: string;
  appliance_id: string;
  appliance_name: string | null;
  appliance_online: boolean;
  endpoint_id: string | null;
  /** The linked endpoint's driver (picks terminal snippets). */
  driver: string | null;
  name: string;
  host: string;
  port: number;
  protocol: TargetProtocol;
  tls_verify: boolean;
  max_session_minutes: number | null;
}

export interface TargetInput {
  appliance_id: string;
  endpoint_id: string | null;
  name: string;
  host: string;
  port: number;
  protocol: TargetProtocol;
  tls_verify: boolean;
  max_session_minutes: number | null;
}

export type SessionKind = 'web' | 'terminal' | 'shell';
export type SessionStatus = 'opening' | 'active' | 'ended' | 'failed';

export interface RemoteSession {
  id: string;
  site_id: string;
  appliance_id: string;
  appliance_name: string | null;
  target_id: string | null;
  target: { name: string; host: string; port: number; protocol: TargetProtocol } | null;
  kind: SessionKind;
  user_id: string;
  user: string;
  mine: boolean;
  reason: string | null;
  status: SessionStatus;
  created_at: string;
  started_at: string | null;
  expires_at: string;
  idle_timeout_seconds: number;
  ended_at: string | null;
  end_reason: string | null;
  bytes_up: number;
  bytes_down: number;
}

export interface Opened {
  session: RemoteSession;
  /** Web sessions: opens the target in a new tab (once, for 2 minutes). */
  url?: string;
  /** Terminal and shell sessions: authenticates the terminal socket. */
  token?: string;
}

export interface Frame {
  ts: string;
  seq: number;
  direction: 'up' | 'down';
  /** Base64. */
  data: string;
  truncated_bytes?: number;
}

export interface Snippet {
  id: string;
  driver: string | null;
  name: string;
  encoding: 'text' | 'hex';
  body: string;
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const hasArray =
  <K extends string>(key: K) =>
  <T>(v: unknown): v is Record<K, T[]> =>
    isObject(v) && Array.isArray(v[key]);
const isTarget = (v: unknown): v is AccessTarget => isObject(v) && typeof v.id === 'string';
const isSnippet = (v: unknown): v is Snippet => isObject(v) && typeof v.id === 'string';

const site = (apiUrl: string, tenantId: string, siteId: string) =>
  `${apiUrl}/v1/tenants/${encodeURIComponent(tenantId)}/sites/${encodeURIComponent(siteId)}`;
const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export async function fetchTargets(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  signal?: AbortSignal,
): Promise<AccessTarget[]> {
  const body = await call(
    `${site(apiUrl, tenantId, siteId)}/access-targets`,
    token,
    { signal },
    hasArray('access_targets'),
  );
  return body.access_targets as AccessTarget[];
}

/** Creates a target, or replaces it when id is given. */
export function saveTarget(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  input: TargetInput,
  id?: string,
): Promise<AccessTarget> {
  const base = `${site(apiUrl, tenantId, siteId)}/access-targets`;
  return call(
    id ? `${base}/${encodeURIComponent(id)}` : base,
    token,
    json(id ? 'PUT' : 'POST', input),
    isTarget,
  );
}

export function deleteTarget(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  id: string,
): Promise<void> {
  return callNoContent(
    `${site(apiUrl, tenantId, siteId)}/access-targets/${encodeURIComponent(id)}`,
    token,
    { method: 'DELETE' },
  );
}

export async function fetchSessions(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  signal?: AbortSignal,
): Promise<RemoteSession[]> {
  const body = await call(
    `${site(apiUrl, tenantId, siteId)}/remote-sessions?state=all&limit=50`,
    token,
    { signal },
    hasArray('sessions'),
  );
  return body.sessions as RemoteSession[];
}

/** Opens a session to a target, or the emergency shell of an appliance. */
export function openSession(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  what: { target_id: string } | { kind: 'shell'; appliance_id: string; reason: string },
): Promise<Opened> {
  return call(
    `${site(apiUrl, tenantId, siteId)}/remote-sessions`,
    token,
    json('POST', what),
    (v): v is Opened => isObject(v) && isObject(v.session),
  );
}

export function endSession(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  id: string,
): Promise<void> {
  return callNoContent(
    `${site(apiUrl, tenantId, siteId)}/remote-sessions/${encodeURIComponent(id)}`,
    token,
    { method: 'DELETE' },
  );
}

export async function fetchTranscript(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  id: string,
  after = 0,
): Promise<Frame[]> {
  const body = await call(
    `${site(apiUrl, tenantId, siteId)}/remote-sessions/${encodeURIComponent(id)}/transcript?after=${String(after)}&limit=5000`,
    token,
    {},
    hasArray('frames'),
  );
  return body.frames as Frame[];
}

const snippetsUrl = (apiUrl: string, tenantId: string) =>
  `${apiUrl}/v1/tenants/${encodeURIComponent(tenantId)}/remote-snippets`;

export async function fetchSnippets(
  apiUrl: string,
  token: string,
  tenantId: string,
  signal?: AbortSignal,
): Promise<Snippet[]> {
  const body = await call(snippetsUrl(apiUrl, tenantId), token, { signal }, hasArray('snippets'));
  return body.snippets as Snippet[];
}

export function saveSnippet(
  apiUrl: string,
  token: string,
  tenantId: string,
  input: Omit<Snippet, 'id'>,
): Promise<Snippet> {
  return call(snippetsUrl(apiUrl, tenantId), token, json('POST', input), isSnippet);
}

export function deleteSnippet(
  apiUrl: string,
  token: string,
  tenantId: string,
  id: string,
): Promise<void> {
  return callNoContent(`${snippetsUrl(apiUrl, tenantId)}/${encodeURIComponent(id)}`, token, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------- bytes

export type LineEnding = 'none' | 'cr' | 'lf' | 'crlf';

export const LINE_ENDINGS: Record<LineEnding, { label: string; bytes: string }> = {
  none: { label: 'None', bytes: '' },
  cr: { label: 'CR', bytes: '\r' },
  lf: { label: 'LF', bytes: '\n' },
  crlf: { label: 'CR LF', bytes: '\r\n' },
};

/** Text as UTF-8 bytes, with the line ending appended. */
export function encodeText(text: string, ending: LineEnding): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(text + LINE_ENDINGS[ending].bytes);
}

/** "0d 0a", "0D0A", or "0x0d,0x0a" as bytes; null if it isn't hex pairs. */
export function parseHex(input: string): Uint8Array<ArrayBuffer> | null {
  const clean = input.replace(/0x/gi, '').replace(/[\s,:]/g, '');
  if (clean.length === 0 || clean.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(clean)) return null;
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(' ');
}

/** Printable ASCII as is; CR, LF, and tab shown as escapes; other bytes as \xNN. */
export function toAscii(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) {
    if (b === 0x0d) out += '\\r';
    else if (b === 0x0a) out += '\\n';
    else if (b === 0x09) out += '\\t';
    else if (b >= 0x20 && b < 0x7f) out += String.fromCharCode(b);
    else out += `\\x${b.toString(16).padStart(2, '0')}`;
  }
  return out;
}

export function fromBase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** A transcript as text for download: one line per frame. */
export function transcriptText(
  frames: { ts: string; direction: 'up' | 'down'; bytes: Uint8Array }[],
): string {
  return frames
    .map((f) => `${f.ts} ${f.direction === 'up' ? '>' : '<'} ${toAscii(f.bytes)}`)
    .join('\n');
}

/** Bytes for people: B, KB, MB. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${String(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** How long a session ran (or has run), for people. */
export function formatDuration(fromIso: string, toIso: string | null, now = new Date()): string {
  const ms = (toIso ? new Date(toIso) : now).getTime() - new Date(fromIso).getTime();
  const min = Math.max(0, Math.round(ms / 60_000));
  if (min < 1) return 'under a minute';
  if (min < 60) return `${String(min)} min`;
  return `${String(Math.floor(min / 60))} h ${String(min % 60)} min`;
}

// ---------------------------------------------------------------- terminal socket

/** ws(s):// URL of the terminal socket for an http(s):// API URL. */
export function terminalUrl(apiUrl: string): string {
  const u = new URL(apiUrl);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.pathname = `${u.pathname.replace(/\/+$/, '')}/v1/remote/terminal`;
  return u.toString();
}

export type TerminalState = 'connecting' | 'ready' | 'closed';

export interface TerminalOptions {
  url: string;
  sessionId: string;
  token: string;
  /** Shell sessions: the initial window size. */
  cols?: number;
  rows?: number;
  onData: (bytes: Uint8Array) => void;
  onState: (state: TerminalState, reason?: string) => void;
  socketFactory?: (url: string) => WebSocket;
}

/** Close codes from the relay (server/internal/relay/terminal.go). */
export const TERMINAL_CLOSE = {
  unauthenticated: 4401,
  ended: 4410,
  replaced: 4409,
  targetClosed: 4000,
} as const;

/**
 * One terminal socket: authenticates with the session token as its first
 * message (never in the URL), then carries raw bytes both ways.
 */
export class TerminalClient {
  private ws: WebSocket;
  private reason: string | undefined;

  constructor(opts: TerminalOptions) {
    this.ws = (opts.socketFactory ?? ((u) => new WebSocket(u)))(opts.url);
    this.ws.binaryType = 'arraybuffer';
    opts.onState('connecting');
    this.ws.onopen = () => {
      this.ws.send(
        JSON.stringify({
          type: 'auth',
          session_id: opts.sessionId,
          token: opts.token,
          cols: opts.cols,
          rows: opts.rows,
        }),
      );
    };
    this.ws.onmessage = (e: MessageEvent) => {
      // binaryType is arraybuffer: anything but text is the target's bytes.
      if (typeof e.data !== 'string') {
        opts.onData(new Uint8Array(e.data as ArrayBuffer));
        return;
      }
      let m: unknown;
      try {
        m = JSON.parse(e.data);
      } catch {
        return;
      }
      if (!isObject(m)) return;
      if (m.type === 'ready') opts.onState('ready');
      if (m.type === 'closed' && typeof m.reason === 'string') this.reason = m.reason;
    };
    this.ws.onclose = (e: CloseEvent) => {
      opts.onState('closed', this.reason ?? closeReason(e.code));
    };
  }

  send(bytes: Uint8Array<ArrayBuffer>) {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(bytes);
  }

  resize(cols: number, rows: number) {
    if (this.ws.readyState === WebSocket.OPEN)
      this.ws.send(JSON.stringify({ type: 'resize', cols, rows }));
  }

  close() {
    this.ws.close();
  }
}

function closeReason(code: number): string {
  switch (code) {
    case TERMINAL_CLOSE.ended:
      return 'The session has ended.';
    case TERMINAL_CLOSE.replaced:
      return 'The terminal was opened in another window.';
    case TERMINAL_CLOSE.unauthenticated:
      return 'This terminal link is no longer valid.';
    default:
      return 'Disconnected.';
  }
}

/** A user-facing message for remote-access API errors. */
export function remoteErrorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
}
